import { FC, useCallback, useEffect, useState } from 'react';
import { Table, Tooltip, Typography } from 'antd';
import styled from 'styled-components';
import ButtonIcon from 'components/buttonIcon';
import Button from 'components/button';
import FrequencyDropdown from 'pages/Reporting/components/ReportingScheduled/FrequencyDropdown';
import InputText from 'pages/Reporting/components/InputText';
import {
  deleteReportScheduleAction,
  downloadReportAction,
  getReportsScheduleAction,
  patchReportScheduleAction,
} from 'contexts/reporting/actions';
import { format } from 'date-fns';
import { IReportSchedule } from 'contexts/reporting/types';
import Notification from 'components/notification';
import { ActionModal } from 'components/modal';
import SpinnerIMG from 'assets/images/spinner.png';
import { Spin } from 'components/loadings/Spinner';
import SortableHeader from 'components/Table/SortableHeader';
import { isMobile } from 'react-device-detect';
import {
  getColumnDirection,
  getNextSortState,
  loadSortState,
  saveSortState,
  toOrdering,
} from 'utils/sorting';
import { SortState, SortStorageKeyParts, UNSORTED } from 'types/sorting';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ScheduledReportsMobileList from 'pages/Reporting/components/ReportingScheduled/ScheduledReportsMobileList';
import PauseSwitchCell from 'pages/Reporting/components/ReportingScheduled/PauseSwitchCell';
import { screenMd } from 'constants/styles/breakpoints';
import Icon from 'components/icons';
import { getPopularReportImg } from 'utils/imageHandling';
import { capitalize } from 'utils/helpers';
import InputSelect from 'components/inputs/inputSelect';
import { InputSelectMultipleWrapper } from 'pages/Reporting/components/styles';
import { InputArrowBtnContainer, InputContainer, StyledInput } from 'components/inputs/styles';
import { Pagination } from 'components/Table';
import {
  CUSTOM_REPORTS_FEATURE_SLUG,
  CUSTOM_REPORT_SCHEDULING_BLOCKED_COPY,
  ENTERPRISE_PLAN_URL,
  REPORTING_TEST_IDS,
  SCHEDULED_REPORTS_RESTRICTED_COPY,
  SCHEDULED_REPORT_NA_VALUE,
} from 'constants/reporting';
import { FEATURE_NOT_AVAILABLE } from 'constants/errors/codes';
const { Text } = Typography;

const formatDate = (date: Date) => {
  return format(date, 'MMM d, yyyy');
};

export const reportKeys = {
  NAME: { key: 'name', updateNotification: 'Report name updated.' },
  FREQUENCY: { key: 'frequency', updateNotification: 'Delivery frequency updated.' },
  IS_PAUSED: { key: 'is_paused', updateNotification: 'Scheduling status updated.' },
  CREATED_AT: { key: 'created_at' },
  LAST_EXECUTED_DATE: { key: 'last_executed_date' },
  LAST_REPORT_ID: { key: 'last_report_id' },
  NEXT_EXECUTION_DATE: { key: 'next_execution_date' },
};

const StyledTableWrapper = styled.div(
  ({ theme }) => `
  position: relative;
  min-height: 500px;
  padding: 20px;

  border: 1px solid var(--dusty-gray);
  background: white;

  ${screenMd} {
    border-radius: 10px;
    padding: 30px;
  }

  .scheduled-title {
    margin: 0 0 12px 0;
  }

  ${screenMd} {
    .scheduled-title {
      margin-bottom: 20px;
    }
  }

  .ant-table {
    font-size: 14px;

    .ant-table-thead {
      > tr {
        > th {
          color: ${theme.default.colors.palette.Business_LightThemeGreyDarker};
          background-color: transparent;
          border-bottom: none !important;

          &::before {
            display: none !important;
          }
        }
      }
    }
  } 

  /* Name column styles to mirror history table */
  .report-name {
    color: var(--black-dark);
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    min-width: 0; /* enable ellipsis in flex */
  }

  .report-subtitle {
    overflow: hidden;
    color: var(--light-gray-darker);
    font-feature-settings: 'liga' off, 'clig' off;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: Lato;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px; /* 116.667% */
  }
`
);

const StyledEmptyStateContainer = styled.div(
  ({ theme }) => `
  display: flex;
  gap: 10px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  width: 100%;

  h2,
  h4 {
    margin: 0 !important;
  }

  & h4 {
    font-size: 20px;
    font-style: normal;
    font-weight: 450;
    line-height: 26px;
    color: ${theme.default.colors.palette.Business_LightThemeGreyDarker};
  }
`
);

const ReadOnlyValue = styled.span`
  display: inline-block;
  padding: 8px 10px;
  color: var(--black-dark);
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
`;

const RestrictedSchedulesFallback = styled(StyledEmptyStateContainer)`
  a {
    color: ${({ theme }) => theme.default.colors.palette.Business_DataVizLilacLighter};
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.default.colors.palette.Business_DataVizLilacLighter};
      outline-offset: 3px;
      border-radius: 2px;
    }
  }
`;

const SCHEDULED_SORT_KEY: SortStorageKeyParts = { table: 'reports', variant: 'scheduled' };
const DEFAULT_SCHEDULED_SORT: SortState = { columnKey: 'created_at', direction: 'desc' };

const handleDownload = async (id: string) => {
  try {
    const reportUrlResponse = await downloadReportAction(id);
    window.open(reportUrlResponse.result.download_url);
  } catch (error) {
    Notification({
      text: 'There has been a problem generating the link to your report.',
      type: 'error',
    });
  }
};

interface ScheduledReportsTableProps {
  onClose?: () => void;
  readOnly?: boolean;
}

const ScheduledReportsTable: FC<ScheduledReportsTableProps> = ({
  onClose = () => undefined,
  readOnly = false,
}) => {
  const { dashboardReportingSorting } = useFlags() || {};
  const [reportsScheduled, setReportsScheduled] = useState<IReportSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<{ id: string; name: string } | null>(null);
  const [sortState, setSortState] = useState<SortState>(() => {
    if (!dashboardReportingSorting) return UNSORTED;
    const saved = loadSortState(SCHEDULED_SORT_KEY);
    return saved.columnKey ? saved : DEFAULT_SCHEDULED_SORT;
  });
  const ordering = toOrdering(sortState);
  const [frequencyFilter, setFrequencyFilter] = useState<string | null>(null);
  const [frequencyFilterLabel, setFrequencyFilterLabel] = useState<string>('All frequencies');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [count, setCount] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [scheduleAccessDenied, setScheduleAccessDenied] = useState(false);

  const isReportBlocked = (record: IReportSchedule) => readOnly && !record.is_popular_report;

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  const handleSortToggle = useCallback(
    (columnKey: string) => {
      if (!dashboardReportingSorting) return;
      setSortState((prev) => {
        const next = getNextSortState(prev, columnKey);
        saveSortState(SCHEDULED_SORT_KEY, next);
        return next;
      });
      setCurrentPage(1);
    },
    [dashboardReportingSorting]
  );

  const fetchScheduledReports = async () => {
    setLoading(true);
    setScheduleAccessDenied(false);
    try {
      const params: {
        ordering?: string;
        frequency?: string;
        page?: number;
        page_size?: number;
        search?: string;
      } = {
        ordering: ordering ?? undefined,
        page: currentPage,
        page_size: pageSize,
      };
      if (frequencyFilter) params.frequency = frequencyFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const response = await getReportsScheduleAction(params);
      setReportsScheduled(response.results);
      setCount(response.count || 0);
      setScheduleAccessDenied(false);
    } catch (error) {
      if (
        readOnly &&
        error?.response?.status === 403 &&
        error?.response?.data?.code === FEATURE_NOT_AVAILABLE &&
        error?.response?.data?.feature === CUSTOM_REPORTS_FEATURE_SLUG
      ) {
        setScheduleAccessDenied(true);
        setReportsScheduled([]);
        setCount(0);
        return;
      }
      Notification({
        text: error.message,
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduledReports();
  }, [ordering, frequencyFilter, currentPage, pageSize, debouncedSearch, readOnly]);

  const handleUpdate = async (id: string, key: string, value: string | boolean) => {
    setLoading(true);
    try {
      await patchReportScheduleAction(id, { [key]: value });

      const notificationKey = Object.values(reportKeys).find((item) => item.key === key);
      const message =
        notificationKey && 'updateNotification' in notificationKey
          ? notificationKey.updateNotification
          : 'Update successful.';

      Notification({
        text: message,
        type: 'success',
      });

      await fetchScheduledReports();
    } catch (error) {
      Notification({
        text: 'Something went wrong while saving your changes. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string, name: string) => {
    setActionModalOpen(true);
    setReportToDelete({ id, name });
  };

  const handleCancel = () => {
    setActionModalOpen(false);
    setReportToDelete(null);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;
    setLoading(true);
    try {
      await deleteReportScheduleAction(reportToDelete.id);
      Notification({
        text: 'Report has been removed from your scheduled reports.',
        type: 'success',
      });
      await fetchScheduledReports();
    } catch (error) {
      Notification({
        text: 'Something went wrong while deleting the report. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
      handleCancel();
    }
  };

  const columns = [
    {
      title: () =>
        dashboardReportingSorting ? (
          <SortableHeader
            label="Report name"
            columnKey="name"
            direction={getColumnDirection(sortState, 'name')}
            onToggle={handleSortToggle}
            disabled={loading}
          />
        ) : (
          <span style={{ fontWeight: 700, fontSize: 14 }}>Report name</span>
        ),
      dataIndex: reportKeys.NAME.key,
      key: reportKeys.NAME.key,
      width: '30%',
      ellipsis: true,
      render: (text: string, record: IReportSchedule) => {
        const isBlockedRow = isReportBlocked(record);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {record.is_popular_report && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'rgba(167, 147, 246, 0.12)',
                }}
              >
                <img
                  src={getPopularReportImg(record.type)}
                  alt={`${record.type} popular report`}
                  width={36}
                  height={36}
                  style={{ display: 'inline-block' }}
                />
              </span>
            )}
            <div style={{ flex: 1 }} className="report-name">
              {record.is_popular_report ? (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Text ellipsis={{ tooltip: text }}>{text}</Text>
                  {(() => {
                    const anyReport = record as unknown as {
                      frequency?: string;
                      date_range?: {
                        from_date: string;
                        to_date: string;
                      };
                    };

                    const frequencyText = anyReport?.frequency
                      ? capitalize(anyReport.frequency)
                      : '';
                    const drSource = anyReport?.date_range || anyReport?.date_range || null;
                    const fromStr = drSource?.from_date ?? null;
                    const toStr = drSource?.to_date ?? null;

                    const parts: string[] = [];
                    if (frequencyText) parts.push(frequencyText);
                    if (fromStr && toStr) {
                      const start = new Date(fromStr);
                      const end = new Date(toStr);
                      const startFmt = format(start, 'yyyy-MM-dd');
                      const endFmt = format(end, 'yyyy-MM-dd');
                      parts.push(`${startFmt} to ${endFmt}`);
                    }

                    if (!parts.length) return null;
                    return <span className="report-subtitle">{parts.join(': ')}</span>;
                  })()}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <InputText
                    value={text}
                    disabled={loading}
                    onSave={(newValue) => handleUpdate(record.id, reportKeys.NAME.key, newValue)}
                  />
                  {isBlockedRow && (
                    <span className="report-subtitle">{CUSTOM_REPORT_SCHEDULING_BLOCKED_COPY}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: () =>
        dashboardReportingSorting ? (
          <SortableHeader
            label="Created date"
            columnKey="created_at"
            direction={getColumnDirection(sortState, 'created_at')}
            onToggle={handleSortToggle}
            disabled={loading}
          />
        ) : (
          <span style={{ fontWeight: 700, fontSize: 14 }}>Created date</span>
        ),
      dataIndex: reportKeys.CREATED_AT.key,
      key: reportKeys.CREATED_AT.key,
      render: (dateString: string) => {
        const formattedDate = formatDate(new Date(dateString));
        return <span style={{ whiteSpace: 'nowrap' }}>{formattedDate}</span>;
      },
    },
    {
      title: 'Delivery frequency',
      dataIndex: reportKeys.FREQUENCY.key,
      key: reportKeys.FREQUENCY.key,
      render: (text: string, record: IReportSchedule) =>
        isReportBlocked(record) ? (
          <ReadOnlyValue>{SCHEDULED_REPORT_NA_VALUE}</ReadOnlyValue>
        ) : (
          <FrequencyDropdown
            value={text}
            disabled={loading}
            onChange={(newValue) => handleUpdate(record.id, reportKeys.FREQUENCY.key, newValue)}
          />
        ),
    },
    {
      title: 'Schedule status',
      dataIndex: reportKeys.IS_PAUSED.key,
      key: reportKeys.IS_PAUSED.key,
      render: (is_paused: boolean, record: IReportSchedule) =>
        isReportBlocked(record) ? (
          <ReadOnlyValue>{SCHEDULED_REPORT_NA_VALUE}</ReadOnlyValue>
        ) : (
          <PauseSwitchCell
            isPaused={is_paused}
            record={record}
            loading={loading}
            onChange={(newValue) => handleUpdate(record.id, reportKeys.IS_PAUSED.key, newValue)}
          />
        ),
    },
    {
      title: '',
      dataIndex: reportKeys.LAST_REPORT_ID.key,
      key: 'actions',
      render: (last_report_id: string, record: IReportSchedule) => (
        <div className="d-flex justify-content-end gap-2">
          <Tooltip
            title={
              last_report_id
                ? 'Download the latest report—access previous versions in report history'
                : 'The latest version isn’t available yet—Once generated, you’ll be able to download it from here'
            }
          >
            <div>
              <ButtonIcon
                disabled={!last_report_id || loading}
                icon="Download"
                size="small"
                $secondary
                onClick={() => handleDownload(record.last_report_id)}
              />
            </div>
          </Tooltip>
          <ButtonIcon
            disabled={loading}
            icon="Trash"
            size="small"
            $secondary
            onClick={() => handleDelete(record.id, record.name)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {!isMobile && (
        <Button className="mb-4" $secondary size="small" onClick={onClose}>
          <Icon icon="ArrowLeft" />
          <span className="d-none d-md-inline">Back</span>
        </Button>
      )}
      {scheduleAccessDenied ? (
        <RestrictedSchedulesFallback
          data-testid={REPORTING_TEST_IDS.scheduledReportsRestrictedFallback}
        >
          <Typography.Title level={2}>{SCHEDULED_REPORTS_RESTRICTED_COPY.title}</Typography.Title>
          <Typography.Title level={4}>
            {SCHEDULED_REPORTS_RESTRICTED_COPY.description}{' '}
            <a href={ENTERPRISE_PLAN_URL} target="_blank" rel="noopener noreferrer">
              {SCHEDULED_REPORTS_RESTRICTED_COPY.learnMore}
            </a>
          </Typography.Title>
        </RestrictedSchedulesFallback>
      ) : !loading && !reportsScheduled.length ? (
        <StyledEmptyStateContainer>
          <Typography.Title level={2}>You haven’t scheduled any reports yet.</Typography.Title>
          <Typography.Title level={4}>
            Get insights delivered straight to your inbox—set up a scheduled report today.
          </Typography.Title>
        </StyledEmptyStateContainer>
      ) : (
        <StyledTableWrapper>
          <Typography.Title level={4} className="scheduled-title">
            Manage my scheduled reports
          </Typography.Title>
          <div className="d-flex align-items-center gap-2 mb-3">
            <div style={{ flex: 1 }}>
              <InputContainer>
                <StyledInput
                  type="text"
                  placeholder="Search your scheduled reports"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <InputArrowBtnContainer style={{ top: '50%', transform: 'translateY(-50%)' }}>
                  <Icon icon="Search" size="small" color="var(--icon-default)" />
                </InputArrowBtnContainer>
              </InputContainer>
            </div>
            <InputSelectMultipleWrapper>
              <InputSelect
                value={frequencyFilterLabel}
                placeHolder="All frequencies"
                options={[
                  { label: 'All frequencies', value: 'all' },
                  { label: 'Weekly', value: 'weekly' },
                  { label: 'Bi-weekly', value: 'biweekly' },
                  { label: 'Monthly', value: 'monthly' },
                  { label: 'Quarterly', value: 'quarterly' },
                ]}
                onChange={(v) => {
                  if (v === 'all') {
                    setFrequencyFilter(null);
                    setFrequencyFilterLabel('All frequencies');
                    setCurrentPage(1);
                    return;
                  }
                  const map: Record<string, string> = {
                    weekly: 'Weekly',
                    biweekly: 'Bi-weekly',
                    monthly: 'Monthly',
                    quarterly: 'Quarterly',
                  };
                  const value = String(v);
                  setFrequencyFilter(value);
                  setFrequencyFilterLabel(map[value] || 'All frequencies');
                  setCurrentPage(1);
                }}
              />
            </InputSelectMultipleWrapper>
          </div>
          {loading && <Spin zIndex="200" src={SpinnerIMG} size="80" />}
          {!!reportsScheduled.length && (
            <>
              {isMobile ? (
                <ScheduledReportsMobileList
                  loading={loading}
                  reports={reportsScheduled}
                  onDeleteReport={handleDelete}
                  onUpdateReport={handleUpdate}
                  onClose={onClose}
                  readOnly={readOnly}
                />
              ) : (
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={reportsScheduled}
                  pagination={false}
                  style={{ opacity: loading ? 0.5 : 1 }}
                />
              )}
              <Pagination
                count={count}
                page={currentPage}
                page_size={pageSize}
                onPagesClick={(value, action) => {
                  switch (action) {
                    case 'page':
                      setCurrentPage(parseInt(value));
                      break;
                    case 'page_size':
                      setPageSize(parseInt(value));
                      setCurrentPage(1);
                      break;
                  }
                }}
              />
            </>
          )}
          <ActionModal
            title="Remove Scheduled Report"
            description={`Are you sure you want to remove "${reportToDelete?.name}" from your scheduled reports?`}
            active={actionModalOpen}
            buttons={[
              { label: 'Cancel', onClick: handleCancel, type: 'secondary' },
              { label: 'Remove report', onClick: confirmDelete, type: 'primary' },
            ]}
            handleCancel={handleCancel}
          />
        </StyledTableWrapper>
      )}
    </>
  );
};

export default ScheduledReportsTable;
